import { cn } from '@/utils/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'
import { Icon } from '../Icon'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap lg:gap-2 gap-1',
  {
    defaultVariants: {
      size: 'md',
      variant: 'primary',
    },
    variants: {
      size: {
        extraMini: 'p-0 rounded-sm ',
        mini: 'p-1 rounded-sm !text-label-XS-regular h-6',
        sm: 'px-5 py-4 !text-label-S-semibold md:px-3 md:py-2 rounded-sm md:!text-label-XS-semibold h-[38px]',
        md: 'px-4 py-3 rounded-sm !text-label-XS-semibold h-10',
        lg: 'px-5 py-4 rounded-sm !text-label-S-semibold h-[49px]',
      },
      variant: {
        primary:
          'bg-blue-20 text-text-inverted hover:opacity-85  disabled:opacity-40 focus:outline-border-focus focus:outline-[1px]',
        primaryInverted:
          'bg-neutral-100 text-text-base hover:bg-neutral-95 disabled:opacity-40 focus:outline-border-focus focus:outline-[1px]',
        secondary:
          'bg-neutral-100 border-[1px] border-solid border-subtle text-text-subtle hover:bg-neutral-90 focus:outline-border-focus focus:outline-[1px]',
        secondaryInverted:
          'text-text-inverted bg-transparent border-subtle-inverted border-solid border-[1px] hover:bg-base-transparent-30 focus:outline-border-focus focus:outline-[1px]',
        ghost:
          'bg-transparent text-text-faded hover:text-text-base hover:underline focus:text-text-faded',
        ghostInverted:
          'bg-transparent text-text-inverted hover:bg-base-transparent-30 focus:bg-base-transparent-10',
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  className,
  size,
  variant,
  ref,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      className={cn(
        className,
        'focus-visible:ring-border-focus',
        buttonVariants({ className, size, variant }),
      )}
      ref={ref}
      {...props}
    >
      {props.children}
    </Comp>
  )
}

export { Button, buttonVariants }

export const ButtonContent: React.FC<{
  showIconLeft?: boolean | null
  iconLeft?: string | null
  label?: string | null
  children?: React.ReactNode | null
  showIconRight?: boolean | null
  iconRight?: string | null
}> = ({ showIconLeft, iconLeft, label, children, showIconRight, iconRight }) => {
  return (
    <React.Fragment>
      {showIconLeft && iconLeft && <Icon className={label ? ' ' : '' + 'size-4'} name={iconLeft} />}
      {label && label}
      {children && children}
      {showIconRight && iconRight && (
        <Icon className={label ? 'ms-0' : '' + 'size-4'} name={iconRight} />
      )}
    </React.Fragment>
  )
}
